import React, { useState } from 'react';
import { Layout, Header, Sidebar, Content, Menu, Button, Avatar, Dropdown } from '../ui';
import type { MenuItem } from '../ui';
import {
  QrCodeIcon,
  ShoppingBagIcon,
  TagIcon,
  CalendarIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  RectangleGroupIcon,
  UsersIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems: MenuItem[] = [
    {
      key: '/scanner',
      icon: <QrCodeIcon className="h-5 w-5" />,
      label: 'Barcode Scanner',
    },
    {
      key: '/dresses',
      icon: <ShoppingBagIcon className="h-5 w-5" />,
      label: 'Dress Management',
    },
    {
      key: '/customers',
      icon: <UsersIcon className="h-5 w-5" />,
      label: 'Customer Management',
    },
    {
      key: '/brands',
      icon: <TagIcon className="h-5 w-5" />,
      label: 'Brand Management',
    },
    {
      key: '/categories',
      icon: <RectangleGroupIcon className="h-5 w-5" />,
      label: 'Category Management',
    },
    {
      key: '/branches',
      icon: <BuildingStorefrontIcon className="h-5 w-5" />,
      label: 'Branch Management',
    },
    {
      key: '/rentals',
      icon: <CalendarIcon className="h-5 w-5" />,
      label: 'Rental Management',
    },
  ];

  const userMenu = (
    <div className="py-1">
      <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
        <div className="flex items-center">
          <UserIcon className="h-4 w-4 mr-2" />
          {user?.email}
        </div>
      </div>
      <button
        onClick={handleLogout}
        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
        Logout
      </button>
    </div>
  );

  return (
    <Layout>
      <div className="flex h-screen">
        <Sidebar 
          width={collapsed ? '64px' : '256px'}
          collapsible
          className="shadow-lg"
        >
          <div className="h-16 flex items-center justify-center border-b border-gray-200 bg-white">
            <img 
              src="/logo.png" 
              alt="Dress Shop Logo" 
              className="h-24 w-auto"
            />
          </div>
          
          <div className="p-4">
            <Menu
              items={menuItems}
              mode="vertical"
              selectedKeys={[location.pathname]}
              onSelect={(key) => navigate(key)}
              collapsed={collapsed}
            />
          </div>
        </Sidebar>
        
        <div className="flex-1 flex flex-col">
          <Header className="shadow-sm">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="md"
                onClick={() => setCollapsed(!collapsed)}
                className="p-2"
              >
                <Bars3Icon className="h-5 w-5" />
              </Button>
              
              <Dropdown overlay={userMenu} placement="bottomRight">
                <div className="flex items-center space-x-2 cursor-pointer p-2 rounded-md hover:bg-gray-100 transition-colors">
                  <Avatar icon={<UserIcon className="h-4 w-4" />} size="sm" />
                  <span className="text-sm text-gray-700">{user?.email}</span>
                </div>
              </Dropdown>
            </div>
          </Header>
          
          <Content padding="lg" className="bg-gray-50">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Outlet />
            </div>
          </Content>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard; 